/**
 * 达人数据大盘统计 API 接口定义与 Client 封装
 * 依据《达人数据大盘 API 前端对接交接文档》
 */

import { logger } from "@/lib/logger";
import { createApiClient } from "./client";

/* ------------------------------------------------------------------ */
/* TypeScript 类型定义                                                */
/* ------------------------------------------------------------------ */

/**
 * 达人大盘统计接口入参
 */
export interface TalentDashboardQueryParams {
  /** 平台过滤（如 tiktok, instagram, youtube, douyin, bilibili） */
  platform?: string;
  /** 赛道过滤（如 beauty, skincare, 3c） */
  category?: string;
  /** 是否仅看可触达账号 */
  has_contact?: boolean;
}

/**
 * 平台分布项
 */
export interface PlatformItem {
  code: string;
  name: string;
  count: number;
  percentage: number;
}

/**
 * 粉丝分桶分布项
 */
export interface FollowerBucketItem {
  bucket_code: "<1k" | "1k-10k" | "10k-100k" | "100k-1M" | ">1M" | string;
  bucket_label?: string;
  bucket_name?: string;
  count: number;
  percentage: number;
}

/**
 * 雷达图单项
 */
export interface RadarDimensionItem {
  key: string;
  code?: string;
  name: string;
  score: number;
}

/**
 * 赛道分布项
 */
export interface TrackDistributionItem {
  category_code: string;
  category_name: string;
  count: number;
  percentage: number;
}

/**
 * 达人大盘完整数据响应模型
 */
export interface TalentDashboardStatsData {
  /** 月度可触达播放量 */
  monthly_reachable_play: {
    count: number;
    unit: string;
  };
  /** 平台构成 */
  platform_composition: {
    total_account_count: number;
    list: PlatformItem[];
  };
  /** 核心 KPI 汇总 */
  kpi_summary: {
    account_count: number;
    account_share_percentage: number;
    total_follower_count: number;
    median_follower_count: number;
    overall_score: number;
    score_base: number;
  };
  /** 粉丝量级分布 */
  follower_bucket_distribution: FollowerBucketItem[];
  /** 五维平均分雷达图 */
  radar_dimensions: {
    max_score: number;
    scale_score: number;
    engagement_score: number;
    cost_score: number;
    activity_score: number;
    reachability_score: number;
    dimensions: RadarDimensionItem[];
  };
  /** 赛道分布 Top 10 */
  track_distribution: TrackDistributionItem[];
}

/* ------------------------------------------------------------------ */
/* 辅助工具函数                                                       */
/* ------------------------------------------------------------------ */

/**
 * 大数值自适应格式化（万 / 亿 或 K / M）
 */
export function formatCompactNumber(value: number | null | undefined, locale = "zh-CN"): string {
  if (!value || value <= 0) return "0";

  if (locale === "zh-CN" || locale === "zh") {
    if (value >= 100_000_000) {
      return `${(value / 100_000_000).toFixed(2).replace(/\.?0+$/, "")} 亿`;
    }
    if (value >= 10_000) {
      return `${(value / 10_000).toFixed(2).replace(/\.?0+$/, "")} 万`;
    }
    return value.toLocaleString("zh-CN");
  }

  // 英文/国际化格式 (K, M, B)
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

/* ------------------------------------------------------------------ */
/* 契约静态 Mock 数据（对齐后端接口示例）                             */
/* ------------------------------------------------------------------ */

export const DEFAULT_TALENT_DASHBOARD_STATS: TalentDashboardStatsData = {
  monthly_reachable_play: {
    count: 972393404,
    unit: "次",
  },
  platform_composition: {
    total_account_count: 5799,
    list: [
      { code: "douyin", name: "抖音", count: 2426, percentage: 41.8 },
      { code: "bilibili", name: "Bilibili", count: 1500, percentage: 25.9 },
      { code: "tiktok", name: "TikTok", count: 995, percentage: 17.2 },
      { code: "xiaohongshu", name: "小红书", count: 461, percentage: 7.9 },
      { code: "instagram", name: "Instagram", count: 417, percentage: 7.2 },
    ],
  },
  kpi_summary: {
    account_count: 5799,
    account_share_percentage: 100.0,
    total_follower_count: 235000000,
    median_follower_count: 39000,
    overall_score: 44,
    score_base: 100,
  },
  follower_bucket_distribution: [
    { bucket_code: "<1k", bucket_label: "<1k", count: 2, percentage: 0.03 },
    { bucket_code: "1k-10k", bucket_label: "1k-10k", count: 706, percentage: 12.18 },
    { bucket_code: "10k-100k", bucket_label: "10k-100k", count: 5091, percentage: 87.79 },
    { bucket_code: "100k-1M", bucket_label: "100k-1M", count: 0, percentage: 0.0 },
    { bucket_code: ">1M", bucket_label: ">1M", count: 0, percentage: 0.0 },
  ],
  radar_dimensions: {
    max_score: 100,
    scale_score: 50,
    engagement_score: 50,
    cost_score: 50,
    activity_score: 42,
    reachability_score: 27,
    dimensions: [
      { key: "scale", name: "规模", score: 50 },
      { key: "engagement", name: "互动", score: 50 },
      { key: "cost", name: "性价比", score: 50 },
      { key: "activity", name: "活跃", score: 42 },
      { key: "reachability", name: "可触达", score: 27 },
    ],
  },
  track_distribution: [
    { category_code: "beauty", category_name: "美妆", count: 2375, percentage: 41.0 },
    { category_code: "3c", category_name: "3C", count: 1438, percentage: 24.8 },
    { category_code: "food", category_name: "美食探店", count: 607, percentage: 10.5 },
    { category_code: "ai", category_name: "AI内容", count: 550, percentage: 9.5 },
    { category_code: "other", category_name: "其他", count: 204, percentage: 3.5 },
    { category_code: "fmcg", category_name: "大消费快消", count: 149, percentage: 2.6 },
    { category_code: "maternal", category_name: "母婴亲子教育", count: 136, percentage: 2.3 },
    { category_code: "travel", category_name: "旅行生活", count: 82, percentage: 1.4 },
    { category_code: "home", category_name: "家居家电", count: 79, percentage: 1.4 },
    { category_code: "pet", category_name: "宠物", count: 73, percentage: 1.3 },
  ],
};

/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* 散点图与数据覆盖度审计模型                                         */
/* ------------------------------------------------------------------ */

export type PlatformFlagType = "douyin" | "bilibili" | "tiktok" | "xiaohongshu" | "instagram";

export interface TalentScatterPoint {
  id: string;
  talent_name: string;
  platform_flag: PlatformFlagType;
  follower_count: number;
  engagement_rate: number;
  /** 对数 X 轴值：log10(follower_count)，用于严密对数坐标均匀绘制 */
  log_follower: number;
}

export interface PlatformCoverageItem {
  platform_flag: PlatformFlagType;
  platform_name: string;
  total_accounts: number;
  account_share_pct: number;
  region_coverage_pct: number;
  track_coverage_pct: number;
  contact_coverage_pct: number;
  is_warning?: boolean;
  warning_tip?: string;
}

/** 后端接口下发的覆盖度原生项 */
export interface BackendDataCoverageItem {
  platform_flag: string;
  total_count: number;
  region_coverage_count: number;
  region_coverage_percentage: number;
  track_coverage_count: number;
  track_coverage_percentage: number;
  contact_coverage_count?: number;
  contact_coverage_percentage?: number;
}

/** 后端接口下发的散点单点原生项 */
export interface BackendScatterPointRaw {
  id: number | string;
  talent_name: string;
  platform_flag: string;
  platform_handle?: string;
  profile_url?: string;
  follower_count: number;
  engagement_rate: number;
}

/** 达人大盘统计接口完整后端报文数据模型 (GET /api/med/talent/dashboard/stats) */
export interface BackendTalentDashboardData extends TalentDashboardStatsData {
  scatter_plot?: {
    total_points: number;
    points: BackendScatterPointRaw[];
  };
  data_coverage?: BackendDataCoverageItem[];
}

export interface TransformedDashboardResult {
  statsData: TalentDashboardStatsData;
  scatterPoints: TalentScatterPoint[];
  coverageData: PlatformCoverageItem[];
  totalPoints?: number;
}

/* ------------------------------------------------------------------ */
/* API 客户端请求骨架                                                 */
/* ------------------------------------------------------------------ */

const talentApiClient = createApiClient(import.meta.env.NEXT_PUBLIC_API_BASE_URL || "");

/**
 * 获取达人大盘统计数据
 * 对应后端接口：GET /med/talent/dashboard/stats
 */
export async function fetchTalentDashboardStats(
  params: TalentDashboardQueryParams = {}
): Promise<BackendTalentDashboardData> {
  try {
    const query = new URLSearchParams();
    if (params.platform && params.platform !== "all" && params.platform !== "全部") {
      query.set("platform", params.platform);
    }
    if (params.category && params.category !== "all" && params.category !== "全部") {
      query.set("category", params.category);
    }
    if (typeof params.has_contact === "boolean") {
      query.set("has_contact", String(params.has_contact));
    }

    const queryString = query.toString();
    const endpoint = `/api/app/med/talent/dashboard/stats${queryString ? `?${queryString}` : ""}`;

    const res = await talentApiClient.get<BackendTalentDashboardData>(endpoint, {
      needAuth: true,
    });

    const isSuccess = res.success === true || res.code === 0 || String(res.code) === "0";
    if (!isSuccess && Number(res.code) === 401) {
      throw new Error(res.msg || "登录已过期，请重新登录");
    }
    if (!isSuccess) {
      throw new Error(res.msg || "获取达人大盘统计失败");
    }
    if (res.data === undefined) {
      throw new Error("响应数据为空");
    }
    return res.data;
  } catch (error) {
    logger.warn("Failed to fetch talent dashboard stats, fallback will be used", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

// 生成 160+ 个高密度大盘散点，真实呈现海量达人聚类与四象限特征
function generateDenseScatterPoints(): TalentScatterPoint[] {
  const platforms: PlatformFlagType[] = [
    "douyin",
    "bilibili",
    "tiktok",
    "xiaohongshu",
    "instagram",
  ];
  const list: TalentScatterPoint[] = [];
  let idCounter = 1;

  // 1. 密集腰部基石群 (100 个点，粉丝 12,000~95,000，互动率 2.5%~14.5%)
  const coreNames = [
    "美妆测评喵",
    "极客说数码",
    "探店小分队",
    "今日OOTD",
    "旅行日记",
    "科技拆解室",
    "护肤研习社",
    "烘焙慢时光",
    "桌搭生活志",
    "粤味厨房",
    "家居收纳师",
    "独立游戏说",
    "健身打卡社",
    "短剧精选坊",
    "复古穿搭集",
    "母婴好物说",
    "城市漫游者",
    "摄影技巧社",
    "手帐日常",
    "自驾越野记",
  ];
  for (let i = 0; i < 100; i++) {
    const p = platforms[i % platforms.length];
    const fans = Math.round(12000 + (Math.sin(i * 3.7) * 0.5 + 0.5) * 82000);
    const eng = +(2.8 + (Math.cos(i * 2.3) * 0.5 + 0.5) * 11.5).toFixed(1);
    list.push({
      id: `t-c-${idCounter++}`,
      talent_name: `${coreNames[i % coreNames.length]}_${(i % 10) + 1}`,
      platform_flag: p,
      follower_count: fans,
      engagement_rate: eng,
      log_follower: +Math.log10(fans).toFixed(3),
    });
  }

  // 2. 左上角高粘性腰尾黑马 (35 个点，粉丝 2,500~45,000，互动率 15.5%~72.0%)
  const darkHorseNames = [
    "小众香氛馆",
    "黑胶发烧友",
    "微观盆景",
    "手作银饰",
    "硬核改模",
    "手冲咖啡客",
    "古着猎人",
    "潮玩涂装",
    "复古骑行",
    "黏土定格",
  ];
  for (let i = 0; i < 35; i++) {
    const p = platforms[i % platforms.length];
    const fans = Math.round(2500 + (Math.sin(i * 4.1) * 0.5 + 0.5) * 40000);
    const eng = +(15.5 + (Math.cos(i * 1.7) * 0.5 + 0.5) * 55.0).toFixed(1);
    list.push({
      id: `t-dh-${idCounter++}`,
      talent_name: `${darkHorseNames[i % darkHorseNames.length]}_${(i % 5) + 1}`,
      platform_flag: p,
      follower_count: fans,
      engagement_rate: eng,
      log_follower: +Math.log10(fans).toFixed(3),
    });
  }

  // 3. 右上角头部高爆款 (15 个点，粉丝 105,000~380,000，互动率 15.2%~38.5%)
  const viralNames = ["脑洞实验室", "全网爆笑王", "极限科学秀", "破圈挑战赛", "超能大侦探"];
  for (let i = 0; i < 15; i++) {
    const p = platforms[i % platforms.length];
    const fans = Math.round(105000 + (Math.sin(i * 2.9) * 0.5 + 0.5) * 260000);
    const eng = +(15.2 + (Math.cos(i * 3.1) * 0.5 + 0.5) * 22.0).toFixed(1);
    list.push({
      id: `t-v-${idCounter++}`,
      talent_name: `${viralNames[i % viralNames.length]}_${(i % 3) + 1}`,
      platform_flag: p,
      follower_count: fans,
      engagement_rate: eng,
      log_follower: +Math.log10(fans).toFixed(3),
    });
  }

  // 4. 右下角品牌泛曝光 (12 个点，粉丝 110,000~450,000，互动率 2.0%~12.5%)
  const reachNames = ["科技资讯社", "百大剪辑站", "影视精选播", "时尚大赏圈"];
  for (let i = 0; i < 12; i++) {
    const p = platforms[i % platforms.length];
    const fans = Math.round(110000 + (Math.sin(i * 1.9) * 0.5 + 0.5) * 320000);
    const eng = +(2.0 + (Math.cos(i * 4.3) * 0.5 + 0.5) * 10.5).toFixed(1);
    list.push({
      id: `t-r-${idCounter++}`,
      talent_name: `${reachNames[i % reachNames.length]}_${(i % 3) + 1}`,
      platform_flag: p,
      follower_count: fans,
      engagement_rate: eng,
      log_follower: +Math.log10(fans).toFixed(3),
    });
  }

  return list;
}

export const MOCK_TALENT_SCATTER_POINTS: TalentScatterPoint[] = generateDenseScatterPoints();

/**
 * 各平台数据覆盖度质量审计矩阵（真实反映 TikTok 地区字段缺失痛点）
 */
export const MOCK_PLATFORM_COVERAGE_DATA: PlatformCoverageItem[] = [
  {
    platform_flag: "douyin",
    platform_name: "抖音",
    total_accounts: 2426,
    account_share_pct: 41.8,
    region_coverage_pct: 98.2,
    track_coverage_pct: 91.5,
    contact_coverage_pct: 45.2,
  },
  {
    platform_flag: "bilibili",
    platform_name: "B站",
    total_accounts: 1500,
    account_share_pct: 25.9,
    region_coverage_pct: 94.6,
    track_coverage_pct: 86.8,
    contact_coverage_pct: 38.6,
  },
  {
    platform_flag: "tiktok",
    platform_name: "TikTok",
    total_accounts: 995,
    account_share_pct: 17.2,
    region_coverage_pct: 5.2, // 业务核心痛点：严重缺失警告
    track_coverage_pct: 82.4,
    contact_coverage_pct: 41.8,
    is_warning: true,
    warning_tip: "批次未调取国家 API，需补标",
  },
  {
    platform_flag: "xiaohongshu",
    platform_name: "小红书",
    total_accounts: 461,
    account_share_pct: 7.9,
    region_coverage_pct: 89.1,
    track_coverage_pct: 93.7,
    contact_coverage_pct: 52.3,
  },
  {
    platform_flag: "instagram",
    platform_name: "Instagram",
    total_accounts: 417,
    account_share_pct: 7.2,
    region_coverage_pct: 76.4,
    track_coverage_pct: 69.2,
    contact_coverage_pct: 34.1,
  },
];

const PLATFORM_NAME_MAP: Record<string, string> = {
  douyin: "抖音",
  bilibili: "B站",
  tiktok: "TikTok",
  xiaohongshu: "小红书",
  instagram: "Instagram",
  youtube: "YouTube",
};

/**
 * 将后端报文真实适配为前端所需数据模型（严格解析后端真实数据）
 */
export function transformDashboardStats(
  raw: BackendTalentDashboardData
): TransformedDashboardResult {
  // 1. 转换 statsData (6大看板数据)
  const statsData: TalentDashboardStatsData = {
    monthly_reachable_play: {
      count: raw.monthly_reachable_play?.count ?? 0,
      unit: raw.monthly_reachable_play?.unit || "次",
    },
    platform_composition: {
      total_account_count: raw.platform_composition?.total_account_count ?? 0,
      list: (raw.platform_composition?.list || []).map((p) => ({
        code: p.code,
        name: PLATFORM_NAME_MAP[p.code] || p.name || p.code,
        count: p.count ?? 0,
        percentage: p.percentage ?? 0,
      })),
    },
    kpi_summary: {
      account_count: raw.kpi_summary?.account_count ?? 0,
      account_share_percentage: raw.kpi_summary?.account_share_percentage ?? 0,
      total_follower_count: raw.kpi_summary?.total_follower_count ?? 0,
      median_follower_count: raw.kpi_summary?.median_follower_count ?? 0,
      overall_score: raw.kpi_summary?.overall_score ?? 0,
      score_base: raw.kpi_summary?.score_base ?? 100,
    },
    follower_bucket_distribution: (raw.follower_bucket_distribution || []).map((b) => ({
      bucket_code: b.bucket_code,
      bucket_label: b.bucket_label || (b as any).bucket_name || b.bucket_code,
      bucket_name: (b as any).bucket_name || b.bucket_label || b.bucket_code,
      count: b.count ?? 0,
      percentage: b.percentage ?? 0,
    })),
    radar_dimensions: {
      max_score: raw.radar_dimensions?.max_score ?? 100,
      scale_score: raw.radar_dimensions?.scale_score ?? 0,
      engagement_score: raw.radar_dimensions?.engagement_score ?? 0,
      cost_score: raw.radar_dimensions?.cost_score ?? 0,
      activity_score: raw.radar_dimensions?.activity_score ?? 0,
      reachability_score: raw.radar_dimensions?.reachability_score ?? 0,
      dimensions: (raw.radar_dimensions?.dimensions || []).map((d: any) => ({
        key: d.key || d.code || "scale",
        code: d.code || d.key || "scale",
        name: d.name || "",
        score: d.score ?? 0,
      })),
    },
    track_distribution: (raw.track_distribution || []).map((t) => ({
      category_code: t.category_code,
      category_name: t.category_name,
      count: t.count ?? 0,
      percentage: t.percentage ?? 0,
    })),
  };

  // 2. 转换散点图点位（基于真实粉丝量自动计算对数 X 轴数值）
  const scatterPoints: TalentScatterPoint[] = (raw.scatter_plot?.points || []).map((p, idx) => {
    const fans = Math.max(1, p.follower_count || 1);
    return {
      id: String(p.id ?? `sp-${idx}`),
      talent_name: p.talent_name || `达人_${idx + 1}`,
      platform_flag: (p.platform_flag as PlatformFlagType) || "douyin",
      follower_count: fans,
      engagement_rate: typeof p.engagement_rate === "number" ? p.engagement_rate : 0,
      log_follower: +Math.log10(fans).toFixed(3),
    };
  });

  // 3. 转换各平台数据资产覆盖度矩阵
  const totalAllAccounts = statsData.platform_composition.total_account_count || 1;
  const coverageData: PlatformCoverageItem[] = (raw.data_coverage || []).map((c) => {
    const pFlag = (c.platform_flag as PlatformFlagType) || "douyin";
    const name = PLATFORM_NAME_MAP[pFlag] || c.platform_flag;
    const sharePct = +((c.total_count / totalAllAccounts) * 100).toFixed(1);
    const regPct =
      typeof c.region_coverage_percentage === "number" ? c.region_coverage_percentage : 0;
    const trackPct =
      typeof c.track_coverage_percentage === "number" ? c.track_coverage_percentage : 0;
    const contactPct =
      typeof c.contact_coverage_percentage === "number" ? c.contact_coverage_percentage : 0;

    return {
      platform_flag: pFlag,
      platform_name: name,
      total_accounts: c.total_count,
      account_share_pct: sharePct,
      region_coverage_pct: regPct,
      track_coverage_pct: trackPct,
      contact_coverage_pct: contactPct,
      is_warning: regPct < 10,
      warning_tip: regPct < 10 ? "地区代码缺失严重" : undefined,
    };
  });

  return {
    statsData,
    scatterPoints,
    coverageData,
    totalPoints: raw.scatter_plot?.total_points ?? scatterPoints.length,
  };
}

/* ------------------------------------------------------------------ */
/* 达人明细列表与检索分页接口 (GET /med/talent/dashboard/talents)       */
/* ------------------------------------------------------------------ */

export interface TalentDashboardTalentQuery {
  page?: number;
  size?: number;
  platform?: string;
  keyword?: string;
  category?: string;
  follower_bucket?: string;
  sort_field?: 'follower_count' | 'engagement_rate' | 'avg_play_count';
  sort_order?: 'desc' | 'asc';
}

export interface TalentDashboardTalentItem {
  id: number | string;
  talent_name: string;
  platform_flag: string;
  platform_handle: string;
  profile_url: string;
  avatar_url?: string;
  follower_count: number;
  content_categories: string[];
  country?: string;
  account_region?: string;
  engagement_rate: number;
  avg_play_count: number;
}

export interface TalentGroupedData {
  under_10k: TalentDashboardTalentItem[];
  from_10k_to_100k: TalentDashboardTalentItem[];
  from_100k_to_1m: TalentDashboardTalentItem[];
  over_1m: TalentDashboardTalentItem[];
}

export interface TalentPageData {
  total: number;
  list: TalentDashboardTalentItem[];
}

export interface TalentQueryResultResp {
  unlocked: boolean;
  grouped_data: TalentGroupedData | null;
  page_data: TalentPageData | null;
}

// 兼容旧接口命名
export type TalentDashboardTalentPageResult = TalentQueryResultResp;

/**
 * 分页查询达人大盘列表或试看样本
 * 对应后端接口：GET /med/talent/dashboard/talents
 */
export async function fetchTalentDashboardTalents(
  params: TalentDashboardTalentQuery = {}
): Promise<TalentQueryResultResp> {
  const query = new URLSearchParams();
  if (params.page && params.page > 0) {
    query.set('page', String(params.page));
  }
  if (params.size && params.size > 0) {
    query.set('size', String(params.size));
  }
  if (params.platform && params.platform !== 'all' && params.platform !== '全部') {
    query.set('platform', params.platform);
  }
  if (params.keyword && params.keyword.trim()) {
    query.set('keyword', params.keyword.trim());
  }
  if (params.category && params.category !== 'all' && params.category !== '全部') {
    query.set('category', params.category);
  }
  if (params.follower_bucket && params.follower_bucket !== 'all' && params.follower_bucket !== '全部') {
    query.set('follower_bucket', params.follower_bucket);
  }
  if (params.sort_field) {
    query.set('sort_field', params.sort_field);
  }
  if (params.sort_order) {
    query.set('sort_order', params.sort_order);
  }

  const queryString = query.toString();
  const endpoint = `/api/app/med/talent/dashboard/talents${queryString ? `?${queryString}` : ''}`;

  const res = await talentApiClient.get<TalentQueryResultResp>(endpoint, {
    needAuth: true,
  });

  const isSuccess = res.success === true || res.code === 0 || String(res.code) === '0';
  if (!isSuccess) {
    throw new Error(res.msg || '获取达人列表失败');
  }

  return res.data || {
    unlocked: false,
    grouped_data: {
      under_10k: [],
      from_10k_to_100k: [],
      from_100k_to_1m: [],
      over_1m: [],
    },
    page_data: null,
  };
}

/* ------------------------------------------------------------------ */
/* 达人预览样本模型（20位公开样本，对齐截图）                          */
/* ------------------------------------------------------------------ */

export interface TalentPreviewSample {
  id: string;
  name: string;
  handle: string;
  platform: PlatformFlagType;
  followerCountStr: string;
  followerCount: number;
  category: string;
  region: string;
  engagementRate: string;
  avgViews: string;
  /** 商业合作参考价（非公开商业机密，未登录时毛玻璃打码） */
  quotePrice: string;
  /** 直接建联商务渠道（非公开核心资产，未登录时毛玻璃打码） */
  contactInfo: string;
}

export const MOCK_TALENT_PREVIEW_SAMPLES: TalentPreviewSample[] = [
  {
    id: "p-1",
    name: "铜了个棉",
    handle: "@Rjldcx05",
    platform: "douyin",
    followerCountStr: "9.4 万",
    followerCount: 94000,
    category: "其他",
    region: "湖北",
    engagementRate: "9.17%",
    avgViews: "4,806",
    quotePrice: "¥ 12,000",
    contactInfo: "wx: rjld_***",
  },
  {
    id: "p-2",
    name: "Banana 083",
    handle: "@6382853",
    platform: "bilibili",
    followerCountStr: "4.8 万",
    followerCount: 48000,
    category: "美妆",
    region: "广东",
    engagementRate: "21.77%",
    avgViews: "5,214",
    quotePrice: "¥ 8,500",
    contactInfo: "bili_bd@***.com",
  },
  {
    id: "p-3",
    name: "烤焦化没能吸阳",
    handle: "@1075508705",
    platform: "douyin",
    followerCountStr: "8.3 万",
    followerCount: 83000,
    category: "美妆",
    region: "河北",
    engagementRate: "7.26%",
    avgViews: "33.0 万",
    quotePrice: "¥ 15,000",
    contactInfo: "wx: sun_ad***",
  },
  {
    id: "p-4",
    name: "特莱雅tobeyoung",
    handle: "@1377062316",
    platform: "bilibili",
    followerCountStr: "4.9 万",
    followerCount: 49000,
    category: "3C",
    region: "广东",
    engagementRate: "9.89%",
    avgViews: "7.4 万",
    quotePrice: "¥ 9,200",
    contactInfo: "tobeyoung@***.cn",
  },
  {
    id: "p-5",
    name: "522",
    handle: "@340778530",
    platform: "douyin",
    followerCountStr: "9.4 万",
    followerCount: 94000,
    category: "美妆",
    region: "山东",
    engagementRate: "8.49%",
    avgViews: "2.4 万",
    quotePrice: "¥ 11,500",
    contactInfo: "wx: hz522_***",
  },
  {
    id: "p-6",
    name: "娃娘奶昔.",
    handle: "@yyrix168",
    platform: "douyin",
    followerCountStr: "9.7 万",
    followerCount: 97000,
    category: "剧情娱乐",
    region: "山西",
    engagementRate: "3.48%",
    avgViews: "14.6 万",
    quotePrice: "¥ 13,000",
    contactInfo: "138****0921",
  },
  {
    id: "p-7",
    name: "aaa思凡",
    handle: "@97556192",
    platform: "bilibili",
    followerCountStr: "4.7 万",
    followerCount: 47000,
    category: "美妆",
    region: "广东",
    engagementRate: "20.70%",
    avgViews: "7,288",
    quotePrice: "¥ 7,800",
    contactInfo: "sifan_biz@***.com",
  },
  {
    id: "p-8",
    name: "神原Nyaru",
    handle: "@5847090",
    platform: "bilibili",
    followerCountStr: "4.9 万",
    followerCount: 49000,
    category: "美妆",
    region: "浙江",
    engagementRate: "27.78%",
    avgViews: "6,273",
    quotePrice: "¥ 9,600",
    contactInfo: "wx: nyaru_***",
  },
  {
    id: "p-9",
    name: "浓青工科男",
    handle: "@551973870",
    platform: "bilibili",
    followerCountStr: "4.8 万",
    followerCount: 48000,
    category: "3C",
    region: "重庆",
    engagementRate: "17.81%",
    avgViews: "5.8 万",
    quotePrice: "¥ 8,800",
    contactInfo: "gk_tech@***.163.com",
  },
  {
    id: "p-10",
    name: "泡泡柚子",
    handle: "@2021051700000000",
    platform: "bilibili",
    followerCountStr: "4.6 万",
    followerCount: 46000,
    category: "美妆",
    region: "江苏",
    engagementRate: "14.27%",
    avgViews: "2.4 万",
    quotePrice: "¥ 7,500",
    contactInfo: "wx: yz_bubble***",
  },
  {
    id: "p-11",
    name: "Elena_DailyVlog",
    handle: "@elena_us",
    platform: "tiktok",
    followerCountStr: "4.5 万",
    followerCount: 45000,
    category: "美妆",
    region: "加利福尼亚",
    engagementRate: "7.40%",
    avgViews: "3.8 万",
    quotePrice: "$ 800",
    contactInfo: "elena.mgmt@***.com",
  },
  {
    id: "p-12",
    name: "TechWithSam",
    handle: "@sam_tech_uk",
    platform: "tiktok",
    followerCountStr: "2.9 万",
    followerCount: 29000,
    category: "3C",
    region: "伦敦",
    engagementRate: "10.40%",
    avgViews: "1.9 万",
    quotePrice: "$ 550",
    contactInfo: "sam.tech.pr@***.co.uk",
  },
  {
    id: "p-13",
    name: "护肤成分党Lin",
    handle: "@lin_skincare",
    platform: "xiaohongshu",
    followerCountStr: "5.1 万",
    followerCount: 51000,
    category: "美妆",
    region: "上海",
    engagementRate: "12.00%",
    avgViews: "4.2 万",
    quotePrice: "¥ 10,000",
    contactInfo: "wx: lin_skin***",
  },
  {
    id: "p-14",
    name: "法式复古小乔",
    handle: "@vintage_qiao",
    platform: "xiaohongshu",
    followerCountStr: "3.4 万",
    followerCount: 34000,
    category: "穿搭",
    region: "北京",
    engagementRate: "15.60%",
    avgViews: "2.8 万",
    quotePrice: "¥ 6,800",
    contactInfo: "qiao_outfit@***.com",
  },
  {
    id: "p-15",
    name: "Chloe_Aesthetic",
    handle: "@chloe_la",
    platform: "instagram",
    followerCountStr: "3.9 万",
    followerCount: 39000,
    category: "美妆",
    region: "洛杉矶",
    engagementRate: "8.60%",
    avgViews: "2.2 万",
    quotePrice: "$ 650",
    contactInfo: "chloe.la.collab@***.com",
  },
  {
    id: "p-16",
    name: "LondonFashionHub",
    handle: "@london_style",
    platform: "instagram",
    followerCountStr: "8.1 万",
    followerCount: 81000,
    category: "穿搭",
    region: "伦敦",
    engagementRate: "5.10%",
    avgViews: "5.6 万",
    quotePrice: "$ 1,200",
    contactInfo: "london.agency@***.com",
  },
  {
    id: "p-17",
    name: "吃货探店王",
    handle: "@foodie_king",
    platform: "douyin",
    followerCountStr: "7.8 万",
    followerCount: 78000,
    category: "美食探店",
    region: "四川",
    engagementRate: "4.90%",
    avgViews: "18.2 万",
    quotePrice: "¥ 14,000",
    contactInfo: "wx: foodie_biz***",
  },
  {
    id: "p-18",
    name: "AI探索官Leon",
    handle: "@ai_leon_bili",
    platform: "bilibili",
    followerCountStr: "4.2 万",
    followerCount: 42000,
    category: "AI内容",
    region: "北京",
    engagementRate: "16.20%",
    avgViews: "8.4 万",
    quotePrice: "¥ 9,000",
    contactInfo: "leon.ai.bd@***.com",
  },
  {
    id: "p-19",
    name: "TokyoLifestyle",
    handle: "@tokyo_life",
    platform: "tiktok",
    followerCountStr: "6.7 万",
    followerCount: 67000,
    category: "生活",
    region: "东京",
    engagementRate: "5.80%",
    avgViews: "4.5 万",
    quotePrice: "¥ 110,000 JPY",
    contactInfo: "tokyo.pr.agency@***.jp",
  },
  {
    id: "p-20",
    name: "手作陶艺匠人",
    handle: "@ceramic_art",
    platform: "xiaohongshu",
    followerCountStr: "4,200",
    followerCount: 4200,
    category: "手作",
    region: "景德镇",
    engagementRate: "68.50%",
    avgViews: "9,800",
    quotePrice: "¥ 3,500",
    contactInfo: "wx: clay_master***",
  },
];
